import {
  createRouter,
  createWebHistory,
  type LocationQuery,
  type LocationQueryRaw,
  type RouteLocationNormalized,
  type RouteLocationRaw,
  type RouteRecordRaw,
} from 'vue-router'
import AppLayout from '@/components/layout/AppLayout.vue'
import { useAuthStore } from '@/stores/auth'
import { useNavigationStore } from '@/stores/navigation'
import { useMetadataStore } from '@/stores/metadata'
import { useProjectsStore } from '@/stores/projects'
import { projectFeatureAdmission } from './projectFeatures'
import {
  isDevBuild,
  localDevelopmentAdminEmail,
  localDevelopmentAdminPassword,
} from '@/utils/isDev'
import {
  extractionCompatibilityGuard,
  extractionQueueQuery,
  fileWorkspaceQuery,
  nativeExtractionTarget,
} from './extractionRoutes'

// Route layout strategy:
//   - /login and the /:pathMatch(.*)* not-found route render WITHOUT the
//     authenticated shell — they're top-level records.
//   - Everything else is a child of the `/` parent route whose component is
//     AppLayout, so the sidebar + main content frame render around the view.
const routes: RouteRecordRaw[] = [
  {
    path: '/map-login',
    name: 'map-login',
    component: () => import('@/views/MapLoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/accept-invite',
    name: 'accept-invite',
    component: () => import('@/views/AcceptInvitationView.vue'),
    meta: { public: true },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { public: true },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: { public: true },
  },
  {
    path: '/portal/login',
    name: 'customer-login',
    component: () => import('@/views/CustomerLoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/customer/login',
    redirect: (to) => ({ name: 'customer-login', query: to.query }),
    meta: { public: true },
  },
  {
    path: '/customer/projects/:id(\\d+)/workbooks',
    redirect: (to) => ({
      name: 'customer-workbooks',
      params: { id: to.params.id },
      query: to.query,
    }),
  },
  {
    path: '/',
    component: AppLayout,
    children: [
      { path: '', redirect: '/projects' },
      {
        path: 'portal/projects/:id(\\d+)/workbooks',
        name: 'customer-workbooks',
        component: () => import('@/views/CustomerWorkbookView.vue'),
        props: (route) => ({ projectId: Number(route.params.id) }),
        meta: { customerPortal: true },
      },
      {
        path: 'portal/projects/:id(\\d+)/files',
        name: 'customer-files',
        component: () => import('@/views/project/SourceFilesWorkspace.vue'),
        props: (route) => ({ projectId: Number(route.params.id), customerPortal: true }),
        meta: { customerPortal: true, isFileWorkspace: true },
      },
      {
        path: 'portal/projects/:id(\\d+)/files/:fileId',
        name: 'customer-file',
        component: () => import('@/views/project/SourceFilesWorkspace.vue'),
        props: (route) => ({
          projectId: Number(route.params.id),
          fileId: String(route.params.fileId),
          customerPortal: true,
        }),
        meta: { customerPortal: true, isFileWorkspace: true },
      },
      {
        path: 'portal/projects/:id(\\d+)/settings',
        name: 'customer-settings',
        component: () => import('@/views/SettingsView.vue'),
        meta: { customerPortal: true },
      },
      {
        path: 'projects',
        name: 'projects',
        component: () => import('@/views/ProjectsView.vue'),
      },
      {
        // Numeric `:id(\\d+)` regex fails fast on URLs like /projects/foo —
        // matches the backend's auto-incrementing integer project ids and
        // sends non-numeric paths to the not-found handler instead of a
        // confusing parse failure inside the view. The detail route is a
        // shell that delegates to one of three nested children; bare
        // `/projects/:id` redirects to the Overview tab.
        path: 'projects/:id(\\d+)',
        name: 'project-detail',
        component: () => import('@/views/ProjectDetailView.vue'),
        redirect: (to) => ({ name: 'project-overview', params: { id: to.params.id } }),
        children: [
          {
            path: 'overview',
            name: 'project-overview',
            component: () => import('@/views/project/ProjectOverviewPanel.vue'),
            props: (route) => ({ projectId: Number(route.params.id) }),
          },
          {
            // The Dashboard tab was folded into the action-first Overview (its
            // tiles now render as the Overview progress strip). Redirect any
            // bookmarked / deep-linked /dashboard URL to Overview so it doesn't
            // hit the not-found catch-all.
            path: 'dashboard',
            redirect: (to) => ({ name: 'project-overview', params: { id: to.params.id } }),
          },
          {
            path: 'files',
            name: 'project-files',
            component: () => import('@/views/project/SourceFilesWorkspace.vue'),
            props: (route) => ({ projectId: Number(route.params.id) }),
            meta: { isFileWorkspace: true },
          },
          {
            path: 'files/:fileId',
            name: 'project-file',
            component: () => import('@/views/project/SourceFilesWorkspace.vue'),
            props: (route) => ({
              projectId: Number(route.params.id),
              fileId: String(route.params.fileId),
            }),
            meta: { isFileWorkspace: true },
          },
          {
            path: 'extractions',
            name: 'project-extractions',
            component: () => import('@/views/project/ExtractionEntryView.vue'),
            props: (route) => ({ projectId: Number(route.params.id) }),
          },
          {
            path: 'extractions-v2',
            name: 'project-extractions-v2',
            redirect: (to) => ({
              name: 'project-extractions',
              params: { id: String(to.params.id) },
              query: extractionQueueQuery(to.query),
            }),
          },
          {
            path: 'extractions-v2/assays/:fileId',
            name: 'project-extractions-v2-assay',
            redirect: (to) =>
              nativeExtractionTarget(
                String(to.params.id),
                String(to.params.fileId),
                'assay',
                to.query,
              ),
          },
          {
            path: 'extractions/assays/:fileId',
            name: 'project-extraction-assay',
            component: () => import('@/views/project/ExtractionEntryView.vue'),
            props: (route) => ({
              projectId: Number(route.params.id),
              fileId: String(route.params.fileId),
              treeKind: 'assay',
            }),
            meta: {
              isExtractionView: true,
            },
          },
          {
            path: 'extractions-v2/channels/:fileId',
            name: 'project-extractions-v2-channel',
            redirect: (to) =>
              nativeExtractionTarget(
                String(to.params.id),
                String(to.params.fileId),
                'channel',
                to.query,
              ),
          },
          {
            path: 'extractions/channels/:fileId',
            name: 'project-extraction-channel',
            component: () => import('@/views/project/ExtractionEntryView.vue'),
            props: (route) => ({
              projectId: Number(route.params.id),
              fileId: String(route.params.fileId),
              treeKind: 'channel',
            }),
            meta: {
              isExtractionView: true,
            },
          },
          {
            // Legacy URL: select the channel queue, preserve compatible list
            // filters, and drop the old run action.
            path: 'custom-extractions',
            name: 'project-custom-extractions',
            redirect: (to) => ({
              name: 'project-extractions',
              params: { id: String(to.params.id) },
              query: {
                ...extractionQueueQuery(to.query),
                type: 'channel',
              },
            }),
          },
          {
            // Operator Review Workbook step (plan Step 8). Mounts the
            // workbook queue. A `?path=` from an explicit selection opens the
            // matching review workspace; filename and snapshot metadata come
            // from that matched deliverable rather than the URL.
            path: 'review-workbook',
            name: 'project-review-workbook',
            component: () => import('@/views/project/ProjectReviewWorkbookPanel.vue'),
            props: (route) => ({ projectId: Number(route.params.id) }),
          },
          {
            // Historical extraction certificate URL. The entry boundary
            // resolves exact references to authorized, materialized V2 files.
            path: 'extractions/:eid/certs/:cert',
            name: 'project-extraction-cert',
            component: () => import('@/views/project/ExtractionEntryView.vue'),
            props: (route) => ({ projectId: Number(route.params.id), legacyDetail: true }),
            meta: { isExtractionView: true },
          },
        ],
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/views/SettingsView.vue'),
      },
      {
        path: 'admin/users',
        name: 'admin-users',
        component: () => import('@/views/AdminUsersView.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/security',
        name: 'admin-security',
        component: () => import('@/views/AdminSecurityView.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'admin/integrations',
        name: 'admin-integrations',
        component: () => import('@/views/AdminIntegrationsView.vue'),
        meta: { requiresAdmin: true },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

const LAZY_ROUTE_RELOAD_KEY = 'moria:router:lazy-route-reload'

const QA_QC_ROUTE_NAMES = new Set([
  'projects',
  'customer-workbooks',
  'customer-settings',
  'customer-files',
  'customer-file',
  'project-extractions',
  'project-files',
  'project-file',
  'project-review-workbook',
  'project-extraction-cert',
  'project-extraction-assay',
  'project-extraction-channel',
])

const LOCAL_DEVELOPMENT_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]'])

interface LocalDevelopmentAuth {
  readonly isAuthenticated: boolean
  loginWithPassword(email: string, password: string): Promise<unknown>
}

export function shouldAutoLoginForLocalDevelopment(
  developmentBuild: boolean,
  environment: string | null | undefined,
  hostname: string,
): boolean {
  return (
    developmentBuild &&
    environment === 'local' &&
    LOCAL_DEVELOPMENT_HOSTS.has(hostname.toLowerCase())
  )
}

export async function attemptLocalDevelopmentLogin(
  auth: LocalDevelopmentAuth,
  enabled: boolean,
): Promise<boolean> {
  if (
    !enabled ||
    auth.isAuthenticated ||
    !localDevelopmentAdminEmail ||
    !localDevelopmentAdminPassword
  )
    return false
  try {
    await auth.loginWithPassword(localDevelopmentAdminEmail, localDevelopmentAdminPassword)
    return true
  } catch {
    // A missing or customized seed user must leave the ordinary login flow usable.
    return false
  }
}

function nonEmptyQueryString(query: LocationQuery, key: string): string | null {
  const value = query[key]
  return typeof value === 'string' && value ? value : null
}

function workbookSelectionQuery(query: LocationQuery): LocationQueryRaw {
  const workbookId = nonEmptyQueryString(query, 'workbook_id')
  if (workbookId) return { workbook_id: workbookId }
  const path = nonEmptyQueryString(query, 'path')
  return path ? { path } : {}
}

export function authenticatedLocalCustomerLoginRedirect(
  to: Pick<RouteLocationNormalized, 'name' | 'query'>,
): RouteLocationRaw | null {
  if (to.name !== 'customer-login') return null
  const projectId = typeof to.query.project_id === 'string' ? to.query.project_id : null
  if (!projectId || !/^\d+$/.test(projectId)) return { name: 'projects' }
  if (to.query.destination === 'settings') {
    return { name: 'customer-settings', params: { id: projectId } }
  }
  if (to.query.destination === 'files') {
    const fileId = typeof to.query.file_id === 'string' ? to.query.file_id : null
    const query = fileWorkspaceQuery(to.query)
    return {
      name: fileId ? 'customer-file' : 'customer-files',
      params: { id: projectId, ...(fileId ? { fileId } : {}) },
      ...(Object.keys(query).length > 0 ? { query } : {}),
    }
  }
  const workbookQuery = workbookSelectionQuery(to.query)
  return {
    name: 'customer-workbooks',
    params: { id: projectId },
    ...(Object.keys(workbookQuery).length > 0 ? { query: workbookQuery } : {}),
  }
}

async function prepareLocalDevelopmentAuthentication(
  auth: ReturnType<typeof useAuthStore>,
  metadata: ReturnType<typeof useMetadataStore>,
): Promise<boolean> {
  if (auth.status === 'idle') await auth.fetchUser()
  const deployment = await metadata.refreshIfStale()
  if (deployment) {
    auth.config = {
      ...deployment.auth,
      ...deployment.capabilities,
      environment: deployment.environment,
    }
  }

  const enabled = shouldAutoLoginForLocalDevelopment(
    isDevBuild,
    deployment?.environment ?? auth.config?.environment,
    window.location.hostname,
  )
  await attemptLocalDevelopmentLogin(auth, enabled)
  return enabled
}

function publicRouteRedirectAfterAuthentication(
  to: Pick<RouteLocationNormalized, 'name' | 'query'>,
  auth: Pick<ReturnType<typeof useAuthStore>, 'isAuthenticated' | 'isCustomerViewer'>,
  localDevelopmentLoginEnabled: boolean,
) {
  if (localDevelopmentLoginEnabled && auth.isAuthenticated && !auth.isCustomerViewer) {
    const customerRedirect = authenticatedLocalCustomerLoginRedirect(to)
    if (customerRedirect) return customerRedirect
  }
  return publicRouteRedirect(to.name, auth.isAuthenticated, auth.isCustomerViewer)
}

export function featureFlagRouteRedirect(
  to: Pick<RouteLocationNormalized, 'meta' | 'params'>,
  isFeatureEnabledOnServer: (key: string) => boolean,
): { name: string; params?: { id: string } } | null {
  const key = to.meta.featureFlag
  if (typeof key !== 'string' || isFeatureEnabledOnServer(key)) return null
  const projectId = typeof to.params.id === 'string' ? to.params.id : null
  return projectId
    ? { name: 'project-extractions', params: { id: projectId } }
    : { name: 'projects' }
}

export function qaQcRouteRedirect(to: {
  name?: unknown
  params: Record<string, unknown>
}): { name: string; params?: { id: string } } | null {
  const name = typeof to.name === 'string' ? to.name : ''
  if (QA_QC_ROUTE_NAMES.has(name)) return null
  const projectId = typeof to.params.id === 'string' ? to.params.id : null
  return projectId
    ? { name: 'project-extractions', params: { id: projectId } }
    : { name: 'projects' }
}

export function customerRouteRedirect(to: {
  name?: string | symbol | null
  params: Record<string, unknown>
  query?: RouteLocationNormalized['query']
}): RouteLocationRaw | null {
  if (to.name?.toString().startsWith('customer-')) return null
  const projectId = typeof to.params.id === 'string' ? to.params.id : null
  if (projectId && (to.name === 'project-files' || to.name === 'project-file')) {
    const query = fileWorkspaceQuery(to.query ?? {})
    return {
      name: to.name === 'project-file' ? 'customer-file' : 'customer-files',
      params: {
        id: projectId,
        ...(to.name === 'project-file' && typeof to.params.fileId === 'string'
          ? { fileId: to.params.fileId }
          : {}),
      },
      ...(Object.keys(query).length > 0 ? { query } : {}),
    }
  }
  return projectId
    ? { name: 'customer-workbooks', params: { id: projectId } }
    : { name: 'customer-login' }
}

export function isLazyRouteImportError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed')
  )
}

function clearLazyRouteReloadMarker(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(LAZY_ROUTE_RELOAD_KEY)
  } catch {
    // Best-effort cleanup only.
  }
}

function recoverLazyRouteImport(to: RouteLocationNormalized): void {
  if (typeof window === 'undefined') return
  const target = to.fullPath || window.location.href
  let previousTarget: string | null = null
  try {
    previousTarget = window.sessionStorage.getItem(LAZY_ROUTE_RELOAD_KEY)
  } catch {
    // Treat storage read failures as no previous reload attempt.
  }
  if (previousTarget === target) {
    clearLazyRouteReloadMarker()
    return
  }
  try {
    window.sessionStorage.setItem(LAZY_ROUTE_RELOAD_KEY, target)
  } catch {
    // Navigate anyway; the marker is only a loop guard for repeated failures.
  }
  window.location.assign(target)
}

// Lazy route modules can fail transiently during local dev server restarts or
// after a browser tab has cached an old Vite module graph. Without recovery,
// Vue Router rejects the navigation and leaves the current view looking frozen.
router.onError((error, to) => {
  if (isLazyRouteImportError(error)) recoverLazyRouteImport(to)
})

// Auth guard.
//
// Strategy:
//   - On the first navigation of a session we lazily hit /auth/me to hydrate
//     the store. Subsequent navigations read the cached result.
//   - Public routes (login, not-found) skip auth checks, except /login itself
//     which kicks already-authed users over to /projects.
//   - Protected routes without a session redirect to /login with a return_to
//     query so the OAuth flow can send the user back.
//   - requiresAdmin routes send non-admins to /projects (simpler than a 403
//     page and enough for Phase 1).
router.beforeEach(async (to) => {
  const auth = useAuthStore()
  const metadata = useMetadataStore()
  // Hydrate the ordinary session and deployment metadata first. A local-only
  // seeded login is attempted only after the deployment identity is known.
  const localDevelopmentLoginEnabled = await prepareLocalDevelopmentAuthentication(auth, metadata)

  if (to.meta.public)
    return publicRouteRedirectAfterAuthentication(to, auth, localDevelopmentLoginEnabled)

  if (!auth.isAuthenticated) return unauthenticatedRouteRedirect(to)

  if (auth.isCustomerViewer && !to.name?.toString().startsWith('customer-')) {
    return customerRouteRedirect(to) ?? true
  }

  // beforeEnter does not run for query-only navigation within an extraction route.
  const extractionAdmission = extractionCompatibilityGuard(to)
  if (extractionAdmission && extractionAdmission !== true) return extractionAdmission

  if (to.meta.requiresAdmin && !auth.isAdmin) {
    return { name: 'projects' }
  }

  const featureRedirect = featureFlagRouteRedirect(to, metadata.isFeatureEnabledOnServer)
  if (featureRedirect) return featureRedirect

  if (auth.isQaQcSpecialist) {
    const redirect = qaQcRouteRedirect(to)
    if (redirect) return redirect
  }

  return (await projectFeatureAdmission(to, auth.isCustomerViewer, useProjectsStore())) ?? true
})

function publicRouteRedirect(
  routeName: unknown,
  isAuthenticated: boolean,
  isCustomerViewer: boolean,
) {
  if (routeName !== 'login' || !isAuthenticated) return true
  return { name: isCustomerViewer ? 'customer-login' : 'projects' }
}

export function unauthenticatedRouteRedirect(
  to: Pick<RouteLocationNormalized, 'name' | 'fullPath' | 'params' | 'query'>,
) {
  if (!to.name?.toString().startsWith('customer-')) {
    return { name: 'login', query: { return_to: to.fullPath } }
  }
  const destination =
    to.name === 'customer-settings'
      ? 'settings'
      : to.name === 'customer-files' || to.name === 'customer-file'
        ? 'files'
        : null
  const query: LocationQueryRaw = {
    project_id: to.params.id,
    ...workbookSelectionQuery(to.query),
  }
  if (destination) query.destination = destination
  if (to.name === 'customer-file') query.file_id = to.params.fileId
  if (destination === 'files' && to.query.q) query.q = to.query.q
  if (destination === 'files' && to.query.scope_id) query.scope_id = to.query.scope_id
  return { name: 'customer-login', query }
}

// Track the previous route so Settings can render a contextual return
// breadcrumb. afterEach runs only on completed navigations, so
// blocked/cancelled redirects (e.g. the auth guard above) won't pollute
// the history. Pinia must be installed before the first navigation — main.ts
// does that, so the lazy useNavigationStore() call here is safe.
router.afterEach((_to, from, failure) => {
  if (!failure) clearLazyRouteReloadMarker()
  useNavigationStore().record(from)
})
